import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { selectUser, updateUser } from './store/userSlice';
import API_BASE_URL from './apiConfig';

// Material Kit 2 React base styles
import colors from "assets/theme/base/colors";
import breakpoints from "assets/theme/base/breakpoints";
import typography from "assets/theme/base/typography";
import boxShadows from "assets/theme/base/boxShadows";
import borders from "assets/theme/base/borders";
import globals from "assets/theme/base/globals";

// Material Kit 2 React helper functions
import boxShadow from "assets/theme/functions/boxShadow";
import hexToRgb from "assets/theme/functions/hexToRgb";
import linearGradient from "assets/theme/functions/linearGradient";
import pxToRem from "assets/theme/functions/pxToRem";
import rgba from "assets/theme/functions/rgba";

// Material Kit 2 React components base styles
import list from "assets/theme/components/list";
import listItem from "assets/theme/components/list/listItem";
import listItemText from "assets/theme/components/list/listItemText";
import card from "assets/theme/components/card";
import cardMedia from "assets/theme/components/card/cardMedia";
import cardContent from "assets/theme/components/card/cardContent";
import button from "assets/theme/components/button";
import iconButton from "assets/theme/components/iconButton";
import input from "assets/theme/components/form/input";
import inputLabel from "assets/theme/components/form/inputLabel";
import inputOutlined from "assets/theme/components/form/inputOutlined";
import textField from "assets/theme/components/form/textField";
import menu from "assets/theme/components/menu";
import menuItem from "assets/theme/components/menu/menuItem";
import switchButton from "assets/theme/components/form/switchButton";
import divider from "assets/theme/components/divider";
import tableContainer from "assets/theme/components/table/tableContainer";
import tableHead from "assets/theme/components/table/tableHead";
import tableCell from "assets/theme/components/table/tableCell";
import linearProgress from "assets/theme/components/linearProgress";
import breadcrumbs from "assets/theme/components/breadcrumbs";
import slider from "assets/theme/components/slider";
import avatar from "assets/theme/components/avatar";
import tooltip from "assets/theme/components/tooltip";
import appBar from "assets/theme/components/appBar";
import tabs from "assets/theme/components/tabs";
import tab from "assets/theme/components/tabs/tab";
import stepper from "assets/theme/components/stepper";
import step from "assets/theme/components/stepper/step";
import stepConnector from "assets/theme/components/stepper/stepConnector";
import stepLabel from "assets/theme/components/stepper/stepLabel";
import stepIcon from "assets/theme/components/stepper/stepIcon";
import select from "assets/theme/components/form/select";
import formControlLabel from "assets/theme/components/form/formControlLabel";
import formLabel from "assets/theme/components/form/formLabel";
import checkbox from "assets/theme/components/form/checkbox";
import radio from "assets/theme/components/form/radio";
import autocomplete from "assets/theme/components/form/autocomplete";
import flatpickr from "assets/theme/components/flatpickr";
import container from "assets/theme/components/container";
import popover from "assets/theme/components/popover";
import buttonBase from "assets/theme/components/buttonBase";
import icon from "assets/theme/components/icon";
import svgIcon from "assets/theme/components/svgIcon";
import link from "assets/theme/components/link";
import dialog from "assets/theme/components/dialog";
import dialogTitle from "assets/theme/components/dialog/dialogTitle";
import dialogContent from "assets/theme/components/dialog/dialogContent";
import dialogContentText from "assets/theme/components/dialog/dialogContentText";
import dialogActions from "assets/theme/components/dialog/dialogActions";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const user = useAppSelector(selectUser);
  const userLoading = useAppSelector(state => state.user.loading);
  const dispatch = useAppDispatch();
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize theme from user preference
  useEffect(() => {
    // Wait for user loading to complete
    if (!userLoading) {
      if (user) {
        setDarkMode(user.dark_theme_enabled || false);
      } else {
        // Default to light theme for non-authenticated users
        setDarkMode(false);
      }
      setLoading(false);
    }
  }, [user, userLoading]);

  // Update theme preference in database
  const toggleTheme = async () => {
    if (!user) return;

    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}/theme`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          dark_theme_enabled: newDarkMode
        })
      });

      if (response.ok) {
        // Update user state in Redux
        dispatch(updateUser({ dark_theme_enabled: newDarkMode }));
      } else {
        // Revert on error
        setDarkMode(darkMode);
      }
    } catch (error) {
      console.error('Failed to update theme preference:', error);
      // Revert on error
      setDarkMode(darkMode);
    }
  };

  // Create Material-UI theme that extends Material Kit
  const theme = createTheme({
    breakpoints: { ...breakpoints },
    palette: {
      mode: darkMode ? 'dark' : 'light',
      ...colors,
      background: {
        default: darkMode ? '#121212' : '#f5f5f5',
        paper: darkMode ? '#1e1e1e' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#ffffff' : '#000000',
        secondary: darkMode ? '#b0b0b0' : '#666666',
      },
    },
    typography: { ...typography },
    boxShadows: { ...boxShadows },
    borders: { ...borders },
    functions: {
      boxShadow,
      hexToRgb,
      linearGradient,
      pxToRem,
      rgba,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ...globals,
          ...flatpickr,
          ...container,
        },
      },
      MuiList: { ...list },
      MuiListItem: { ...listItem },
      MuiListItemText: { ...listItemText },
      MuiCard: { 
        ...card,
        styleOverrides: {
          ...card.styleOverrides,
          root: {
            ...card.styleOverrides.root,
            backgroundColor: darkMode ? '#1e1e1e' : card.styleOverrides.root.backgroundColor,
            border: darkMode ? `1px solid ${rgba('#ffffff', 0.12)}` : card.styleOverrides.root.border,
          },
        },
      },
      MuiCardMedia: { ...cardMedia },
      MuiCardContent: { ...cardContent },
      MuiButton: { ...button },
      MuiIconButton: { ...iconButton },
      MuiInput: { ...input },
      MuiInputLabel: { ...inputLabel },
      MuiOutlinedInput: { 
        ...inputOutlined,
        styleOverrides: {
          ...inputOutlined.styleOverrides,
          root: {
            ...inputOutlined.styleOverrides.root,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? '#90caf9' : '#1976d2',
            },
          },
        },
      },
      MuiTextField: { 
        ...textField,
        styleOverrides: {
          ...textField.styleOverrides,
          root: {
            ...textField.styleOverrides.root,
            '& .MuiInputLabel-root': {
              color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            },
            '& .MuiInputBase-input': {
              color: darkMode ? '#ffffff' : '#000000',
            },
            '& .MuiFormHelperText-root': {
              color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            },
          },
        },
      },
      MuiMenu: {
        ...menu,
        styleOverrides: {
          ...menu.styleOverrides,
          paper: {
            ...menu.styleOverrides.paper,
            backgroundColor: darkMode ? '#1e1e1e !important' : menu.styleOverrides.paper.backgroundColor,
            color: darkMode ? '#ffffff' : menu.styleOverrides.paper.color,
          },
        },
      },
      MuiMenuItem: {
        ...menuItem,
        styleOverrides: {
          ...menuItem.styleOverrides,
          root: {
            ...menuItem.styleOverrides.root,
            ...(darkMode
              ? {
                  color: '#ffffff',
                  '&:hover, &:focus, &.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    color: '#ffffff',
                  },
                }
              : {}),
          },
        },
      },
      MuiSwitch: { ...switchButton },
      MuiDivider: { ...divider },
      MuiTableContainer: { ...tableContainer },
      MuiTableHead: { ...tableHead },
      MuiTableCell: { ...tableCell },
      MuiLinearProgress: { ...linearProgress },
      MuiBreadcrumbs: { ...breadcrumbs },
      MuiSlider: { ...slider },
      MuiAvatar: { ...avatar },
      MuiTooltip: { ...tooltip },
      MuiAppBar: { ...appBar },
      MuiTabs: { ...tabs },
      MuiTab: { ...tab },
      MuiStepper: { ...stepper },
      MuiStep: { ...step },
      MuiStepConnector: { ...stepConnector },
      MuiStepLabel: { ...stepLabel },
      MuiStepIcon: { ...stepIcon },
      MuiSelect: { ...select },
      MuiFormControlLabel: { 
        ...formControlLabel,
        styleOverrides: {
          ...formControlLabel.styleOverrides,
          root: {
            ...formControlLabel.styleOverrides.root,
            '& .MuiFormControlLabel-label': {
              color: darkMode ? '#ffffff' : '#000000',
            },
          },
        },
      },
      MuiFormLabel: { ...formLabel },
      MuiCheckbox: { ...checkbox },
      MuiRadio: { ...radio },
      MuiAutocomplete: { ...autocomplete },
      MuiPopover: { ...popover },
      MuiButtonBase: { ...buttonBase },
      MuiIcon: { ...icon },
      MuiSvgIcon: { ...svgIcon },
      MuiLink: { ...link },
      MuiDialog: { ...dialog },
      MuiDialogTitle: { ...dialogTitle },
      MuiDialogContent: { ...dialogContent },
      MuiDialogContentText: { ...dialogContentText },
      MuiDialogActions: { ...dialogActions },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: darkMode ? '#ffffff' : '#000000',
          },
          h1: {
            color: darkMode ? '#ffffff' : '#000000',
          },
          h2: {
            color: darkMode ? '#ffffff' : '#000000',
          },
          h3: {
            color: darkMode ? '#ffffff' : '#000000',
          },
          h4: {
            color: darkMode ? '#ffffff' : '#000000',
          },
          h5: {
            color: darkMode ? '#ffffff' : '#000000',
          },
          h6: {
            color: darkMode ? '#ffffff' : '#000000',
          },
          body1: {
            color: darkMode ? '#ffffff' : '#000000',
          },
          body2: {
            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
            color: darkMode ? '#ffffff' : '#000000',
          },
        },
      },
    },
  });

  const value = {
    darkMode,
    toggleTheme,
    loading
  };

  if (loading) {
    return <div>Loading theme...</div>;
  }

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 